/**
 * Toast Notification System
 * 
 * This module provides a toast notification system inspired by react-hot-toast.
 * It includes functionality for displaying, updating, and dismissing toast notifications
 * with a limit on the number of concurrent toasts.
 * 
 * Features:
 * - Toast limit management
 * - Automatic toast removal
 * - Toast state management
 * - Toast action handling
 * - Global toast state
 */

'use client';

// Inspired by react-hot-toast library
import * as React from 'react';
import type { ToastActionElement, ToastProps } from '@/components/ui/toast';

/** Maximum number of concurrent toasts */
const TOAST_LIMIT = 1;
/** Delay before removing a toast (in milliseconds) */
const TOAST_REMOVE_DELAY = 1000000;

/**
 * Extended toast properties including ID and optional fields
 */
type ToasterToast = ToastProps & {
	/** Unique identifier for the toast */
	id: string;
	/** Optional toast title */
	title?: React.ReactNode;
	/** Optional toast description */
	description?: React.ReactNode;
	/** Optional toast action element */
	action?: ToastActionElement;
};

/**
 * Toast properties without ID
 */
type Toast = Omit<ToasterToast, 'id'>;

/**
 * Toast state containing all active toasts
 */
type ToastState = {
	/** Array of active toasts */
	toasts: ToasterToast[];
};

/**
 * Actions that can be performed on toasts
 */
type ToastAction =
	| { type: 'ADD_TOAST'; toast: ToasterToast }
	| { type: 'UPDATE_TOAST'; toast: Partial<ToasterToast> }
	| { type: 'DISMISS_TOAST'; toastId?: string }
	| { type: 'REMOVE_TOAST'; toastId?: string };

/** Map of toast IDs to their removal timeouts */
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Adds a toast to the removal queue
 * @param {string} toastId - The ID of the toast to remove
 */
const addToRemoveQueue = (toastId: string) => {
	if (toastTimeouts.has(toastId)) {
		return;
	}

	const timeout = setTimeout(() => {
		toastTimeouts.delete(toastId);
		dispatch({
			type: 'REMOVE_TOAST',
			toastId,
		});
	}, TOAST_REMOVE_DELAY);

	toastTimeouts.set(toastId, timeout);
};

/**
 * Reducer function for managing toast state
 * @param {ToastState} state - Current toast state
 * @param {ToastAction} action - Action to perform
 * @returns {ToastState} New toast state
 */
export const reducer = (state: ToastState, action: ToastAction): ToastState => {
	switch (action.type) {
		case 'ADD_TOAST':
			return {
				...state,
				toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
			};

		case 'UPDATE_TOAST':
			return {
				...state,
				toasts: state.toasts.map((t) =>
					t.id === action.toast.id ? { ...t, ...action.toast } : t
				),
			};

		case 'DISMISS_TOAST': {
			const { toastId } = action;

			if (toastId) {
				addToRemoveQueue(toastId);
			} else {
				state.toasts.forEach((toast) => {
					addToRemoveQueue(toast.id);
				});
			}

			return {
				...state,
				toasts: state.toasts.map((t) =>
					t.id === toastId || toastId === undefined
						? {
								...t,
								open: false,
						  }
						: t
				),
			};
		}

		case 'REMOVE_TOAST':
			if (action.toastId === undefined) {
				return {
					...state,
					toasts: [],
				};
			}
			return {
				...state,
				toasts: state.toasts.filter((t) => t.id !== action.toastId),
			};
	}
};

/** Array of state change listeners */
const listeners: Array<(state: ToastState) => void> = [];

/** In-memory toast state */
let memoryState: ToastState = { toasts: [] };

/**
 * Dispatches an action to update the toast state
 * @param {ToastAction} action - Action to dispatch
 */
function dispatch(action: ToastAction) {
	memoryState = reducer(memoryState, action);
	listeners.forEach((listener) => {
		listener(memoryState);
	});
}

/**
 * Creates and displays a new toast
 * @param {Toast} props - Toast properties
 * @returns {Object} Object containing toast ID and control methods
 * 
 * @example
 * const { id, dismiss, update } = toast({
 *   title: 'Success',
 *   description: 'Operation completed successfully',
 *   variant: 'default'
 * });
 * 
 * // Update the toast
 * update({ description: 'Updated message' });
 * 
 * // Dismiss the toast
 * dismiss();
 */
function toast({ ...props }: Toast) {
	const id = crypto.randomUUID();

	const update = (props: ToasterToast) =>
		dispatch({
			type: 'UPDATE_TOAST',
			toast: { ...props, id },
		});

	const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });

	dispatch({
		type: 'ADD_TOAST',
		toast: {
			...props,
			id,
			open: true,
			onOpenChange: (open: boolean) => {
				if (!open) dismiss();
			},
		},
	});

	return {
		id,
		dismiss,
		update,
	};
}

/**
 * Hook for managing toasts
 * @returns {Object} Object containing toast state and control methods
 * 
 * @example
 * const { toasts, toast, dismiss } = useToast();
 * 
 * // Display a toast
 * toast({
 *   title: 'Error',
 *   description: 'Something went wrong',
 *   variant: 'destructive'
 * });
 * 
 * // Dismiss a specific toast
 * dismiss(toastId);
 * 
 * // Dismiss all toasts
 * dismiss();
 */
function useToast() {
	const [state, setState] = React.useState<ToastState>(memoryState);

	React.useEffect(() => {
		listeners.push(setState);
		return () => {
			const index = listeners.indexOf(setState);
			if (index > -1) {
				listeners.splice(index, 1);
			}
		};
	}, [state]);

	return {
		...state,
		toast,
		dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
	};
}

export { useToast, toast };
